import React, { useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichTextEditor from "@/components/RichTextEditor";
import CoverPhotoUploader from "@/components/CoverPhotoUploader";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Newspaper, Pencil, Trash2, Loader2, Plus, X } from "lucide-react";

const emptyForm = { title: "", content: "", category: "Mercado", is_published: true, cover_photo: null };

export default function AdminNews() {
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: a }, { data: c }] = await Promise.all([
        api.get("/news/admin/all"),
        api.get("/news/categories"),
      ]);
      setArticles(a);
      setCategories(c);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startEdit = (a) => {
    setEditingId(a.article_id);
    setForm({
      title: a.title,
      content: a.content,
      category: a.category,
      is_published: !!a.is_published,
      cover_photo: a.cover_photo || null,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const submit = async (e) => {
    e.preventDefault();
    // Quill emits "<p><br></p>" for empty content — treat as blank.
    const plain = (form.content || "").replace(/<[^>]*>/g, "").trim();
    if (!plain) {
      toast.error("El contenido no puede estar vacío");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/news/${editingId}`, form);
        toast.success("Artículo actualizado");
      } else {
        await api.post("/news", form);
        toast.success("Artículo publicado");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (article_id) => {
    try {
      await api.delete(`/news/${article_id}`);
      toast.success("Artículo eliminado");
      setArticles((prev) => prev.filter((a) => a.article_id !== article_id));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-10 pt-8 md:pt-16 pb-16" data-testid="admin-news-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-secondary inline-flex items-center gap-2">
            <Newspaper className="h-3 w-3" /> Panel del Administrador
          </div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl">Noticias Inmobiliarias</h1>
          <p className="mt-2 text-muted-foreground">Solo el administrador puede publicar artículos aquí.</p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={submit} className="space-y-5 border border-border rounded-sm p-5 md:p-8 mb-12" data-testid="admin-news-form">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-serif text-2xl">{editingId ? "Editar artículo" : "Nuevo artículo"}</h2>
          {editingId && (
            <Button type="button" variant="ghost" size="sm" onClick={resetForm} data-testid="cancel-edit">
              <X className="h-4 w-4 mr-1.5" /> Cancelar edición
            </Button>
          )}
        </div>

        <div>
          <label className="label-eyebrow block mb-2">Título</label>
          <Input
            required maxLength={200}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="news-form-title"
            placeholder="Ej: El mercado inmobiliario dominicano en 2026"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow block mb-2">Categoría</label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="news-form-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Estado</label>
            <div className="flex items-center gap-2 h-10">
              <button
                type="button"
                onClick={() => setForm({ ...form, is_published: !form.is_published })}
                data-testid="news-form-published-toggle"
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors ${form.is_published ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
              >
                {form.is_published ? "Publicado" : "Borrador"}
              </button>
              <span className="text-xs text-muted-foreground">
                {form.is_published ? "Visible en /noticias" : "Oculto al público"}
              </span>
            </div>
          </div>
        </div>

        <div>
          <label className="label-eyebrow block mb-2">Contenido</label>
          <RichTextEditor
            value={form.content}
            onChange={(html) => setForm({ ...form, content: html })}
            placeholder="Escribe el artículo. Usa la barra para poner negritas, subtítulos (H2, H3) y ajustar el tamaño del texto."
          />
        </div>

        <div className="border-t border-border pt-6">
          <CoverPhotoUploader
            value={form.cover_photo}
            onChange={(path) => setForm({ ...form, cover_photo: path })}
          />
        </div>

        <div className="flex gap-3">
          <Button type="submit" disabled={saving} className="rounded-full btn-hover-lift" data-testid="news-form-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
            {editingId ? "Guardar cambios" : "Publicar artículo"}
          </Button>
        </div>
      </form>

      {/* Existing articles */}
      <div>
        <h2 className="font-serif text-3xl mb-4">Artículos existentes</h2>
        {loading ? (
          <div className="py-10 text-center text-muted-foreground text-sm">Cargando…</div>
        ) : articles.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-border rounded-sm">
            <p className="text-muted-foreground" data-testid="admin-news-empty">Aún no has publicado artículos.</p>
          </div>
        ) : (
          <div className="grid gap-3" data-testid="admin-news-list">
            {articles.map((a) => (
              <div
                key={a.article_id}
                className="grid grid-cols-[1fr,auto] gap-4 items-center border border-border p-4 rounded-sm hover:bg-accent/20 transition-colors"
                data-testid={`admin-news-row-${a.article_id}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="label-eyebrow text-secondary">{a.category}</span>
                    {!a.is_published && (
                      <span className="text-[10px] uppercase tracking-widest bg-muted text-muted-foreground px-2 py-0.5 rounded-full">Borrador</span>
                    )}
                  </div>
                  <h3 className="font-serif text-xl truncate mt-1">{a.title}</h3>
                  <p className="text-xs text-muted-foreground mt-1">/noticias/{a.slug}</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => startEdit(a)} data-testid={`edit-${a.article_id}`}>
                    <Pencil className="h-3.5 w-3.5 mr-1.5" /> Editar
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-${a.article_id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Eliminar
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Eliminar artículo</AlertDialogTitle>
                        <AlertDialogDescription>“{a.title}” será eliminado permanentemente.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(a.article_id)} data-testid={`confirm-delete-${a.article_id}`}>
                          Eliminar
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
